import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ApiError } from '../../../../lib/api';
import { firebaseAuth } from '../../../../lib/firebase';
import { assetsListHref } from '../../../../lib/portalAssets/pageState';
import { assetDisplayName } from '../../../../lib/portalAssets/gridView';
import {
  AssetDetailView,
  categoriesState,
  AssetHistoryList,
  DeleteAssetControl,
  ReviewControls,
  useImageErrorRefetch,
} from '../../../portalAssets';
import { PortalShell } from '../PortalShell';
import { portalAssetsApi } from './portalAssetsApi';

// /portal/assets/:assetId — one asset (spec §8 "Detail").
//
// ⚠️ A PAGE, not the spec's dialog. A portaled dialog renders OUTSIDE
// `.kyck-portal` and takes the STAFF tokens (CLAUDE.md, the portaled-content
// note), and on a phone a full page is the better surface anyway. It is also a
// link that can be shared within the club.
//
// ⚠️ A 404 here is every refusal alike — missing or another club's — so the
// page says "not found" and nothing more. A DELETED asset in the club's own
// scope still opens (it says it is deleted and that Kyck can restore it).
//
// ⚠️ A club may DELETE any asset in its scope, approved or not (Scott,
// 2026-10-08); the confirmation warns when it is approved. It cannot restore.
// ⚠️ A club may also REVIEW (approve / not approve, with a reason Kyck reads):
// a club and Kyck are equal authorities, so the page shows the HISTORY too.

export function PortalAssetDetailPage() {
  const { assetId = '' } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['portal-assets', 'detail', assetId],
    queryFn: () => portalAssetsApi.detail(assetId),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (tags: string[]) => portalAssetsApi.patch(assetId, { tags }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['portal-assets'] });
    },
  });
  const saveTitle = useMutation({
    mutationFn: (title: string | null) => portalAssetsApi.patch(assetId, { title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['portal-assets'] });
    },
  });
  const accountId = detail.data?.accountId;
  // ⚠️ The brand is derived server-side from this account; no brand id is sent.
  const categories = useQuery({
    queryKey: ['portal-assets', 'categories', accountId],
    queryFn: () => portalAssetsApi.categories(accountId!),
    enabled: accountId !== undefined,
    retry: false,
  });
  const saveCategories = useMutation({
    mutationFn: (categoryIds: string[]) => portalAssetsApi.patch(assetId, { categoryIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['portal-assets'] });
    },
  });
  const history = useQuery({
    queryKey: ['portal-assets', 'history', assetId],
    queryFn: () => portalAssetsApi.history(assetId),
    retry: false,
  });
  const review = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; reason?: string }) => portalAssetsApi.review(assetId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['portal-assets'] });
    },
  });
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: () => portalAssetsApi.remove(assetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['portal-assets'] });
      navigate(assetsListHref());
    },
  });
  const onImageError = useImageErrorRefetch(detail.refetch);

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav>
      <Link to={assetsListHref()} className="text-sm text-muted-foreground underline">
        ← All assets
      </Link>
      <div className="mt-4">
        {detail.isPending ? (
          <CenteredSpinner />
        ) : detail.isError ? (
          <p className="text-sm text-muted-foreground" data-testid="asset-not-found">
            {detail.error instanceof ApiError && (detail.error.status === 404 || detail.error.status === 403)
              ? "This asset isn't available to your login."
              : "We couldn't load this asset. Try again in a moment."}
          </p>
        ) : (
          <>
            <h1 className="mb-4 break-all text-xl font-semibold text-foreground">{assetDisplayName(detail.data)}</h1>
            <div className="mb-4 space-y-3">
              <ReviewControls
                key={detail.data.review.status + (detail.data.review.reason ?? '')}
                audience="client"
                tile={detail.data}
                onReview={(input) => review.mutate(input)}
                pending={review.isPending}
                error={review.error ? review.error.message : null}
              />
              <DeleteAssetControl
                tile={detail.data}
                audience="client"
                onDelete={() => remove.mutate()}
                pending={remove.isPending}
                error={remove.error ? remove.error.message : null}
              />
            </div>
            <AssetDetailView
              key={detail.data.id + (detail.data.title ?? '') + detail.data.tags.join(',') + detail.data.categoryIds.join(',')}
              detail={detail.data}
              audience="client"
              onSaveTitle={(title) => saveTitle.mutate(title)}
              titleSaving={saveTitle.isPending}
              titleError={saveTitle.error ? saveTitle.error.message : null}
              onSaveTags={(tags) => save.mutate(tags)}
              saving={save.isPending}
              saveError={save.error ? save.error.message : null}
              categories={categoriesState(categories)}
              onSaveCategories={(ids) => saveCategories.mutate(ids)}
              categoriesSaving={saveCategories.isPending}
              categoriesError={saveCategories.error ? saveCategories.error.message : null}
              onImageError={onImageError}
              // AI suggestion (Run D): clubs have the same access as staff (Scott). On-demand: only the button calls this.
              onEnrich={() => portalAssetsApi.enrich(assetId)}
              onAcceptEnrichment={(patch) =>
                portalAssetsApi.patch(assetId, patch).then(() => qc.invalidateQueries({ queryKey: ['portal-assets'] }))
              }
            />
            <div className="mt-6">
              <AssetHistoryList entries={history.data?.entries} loading={history.isPending} failed={history.isError} />
            </div>
          </>
        )}
      </div>
    </PortalShell>
  );
}
